<script setup>
import OrganizationTopBar from './OrganizationTopbar.vue';
import api from '@/api/Acutame';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { ref, onBeforeMount, watch } from 'vue';
import * as _ from 'lodash';

const toast = useToast();
const route = useRoute();
const organizationId = route.params.id;

const dt = ref(null);
const customers = ref([]);
const totalElements = ref(0);
const searchFilter = ref('');
const loading = ref(false);
const expandedRows = ref([]);
const customerBookings = ref({});
const loadingBookings = ref({});

const onPageChange = (event) => {
  searchCustomers(event.page);
};

const debouncedSearch = _.debounce(async (page = 0) => {
  const size = dt.value?.rows ?? 10;
  const filter = searchFilter.value;
  
  try {
    const response = await api.searchCustomersForOrg(
      organizationId,
      filter,
      page,
      size
    );
    customers.value = response.content;
    totalElements.value = response.totalElements;
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al buscar clientes',
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
}, 250);

const searchCustomers = async (page = 0) => {
  loading.value = true;
  debouncedSearch(page);
};

const onRowExpand = async (event) => {
  const customerId = event.data.id;
  
  if (!customerBookings.value[customerId]) {
    loadingBookings.value[customerId] = true;
    
    try {
      const response = await api.getCustomerBookings(customerId, 0, 20);
      customerBookings.value[customerId] = response.bookings;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al cargar reservas del cliente',
        life: 3000,
      });
    } finally {
      loadingBookings.value[customerId] = false;
    }
  }
};

const formatPhone = (customer) => {
  if (!customer.phone) return '-';
  return customer.phone;
};

const getCustomerName = (customer) => {
  const parts = [];
  if (customer.firstname) parts.push(customer.firstname);
  if (customer.lastname) parts.push(customer.lastname);
  return parts.length > 0 ? parts.join(' ') : '-';
};

const getFilteredMetafields = (customFields) => {
  if (!customFields) return {};
  
  // Filter out fields that are already shown in customer data
  const excludeFields = ['firstname', 'lastname', 'email', 'phone', 'dialCode'];
  const filtered = {};
  
  Object.keys(customFields).forEach(key => {
    if (!excludeFields.includes(key)) {
      filtered[key] = customFields[key];
    }
  });
  
  return filtered;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStateSeverity = (state) => {
  const severities = {
    ACCEPTED: 'success',
    PENDING: 'warning',
    REJECTED: 'danger',
    CANCELLED: 'info',
  };
  return severities[state] || 'info';
};

const getStateLabel = (state) => {
  const labels = {
    ACCEPTED: 'Aceptada',
    PENDING: 'Pendiente',
    REJECTED: 'Rechazada',
    CANCELLED: 'Cancelada',
  };
  return labels[state] || state;
};

const onSearchFilterChange = () => {
  searchCustomers(0);
};

onBeforeMount(() => searchCustomers());
watch([searchFilter], onSearchFilterChange);
</script>

<template>
  <Toast />
  <div class="grid">
    <div class="col-12">
      <div class="card">
        <div class="containerClass">
          <OrganizationTopBar />
        </div>
        <DataTable
          :value="customers"
          :paginator="true"
          dataKey="id"
          ref="dt"
          :rowHover="true"
          :loading="loading"
          responsiveLayout="scroll"
          :rows="10"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[5, 10, 20, 40]"
          currentPageReportTemplate="Mostrando del {first}º al {last}º de {totalRecords} clientes"
          :totalRecords="totalElements"
          @page="onPageChange($event)"
          :lazy="true"
          v-model:expandedRows="expandedRows"
          @row-expand="onRowExpand"
        >
          <template #header>
            <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
              <h5 class="m-0">Clientes</h5>
              <span class="block mt-2 md:mt-0 p-input-icon-left">
                <i class="pi pi-search" />
                <InputText
                  v-model="searchFilter"
                  placeholder="Buscar por nombre, email o teléfono"
                  style="width: 300px"
                />
              </span>
            </div>
          </template>
          <template #empty> No se han encontrado clientes. </template>
          <template #loading>
            Cargando datos de clientes. Por favor espere.
          </template>
          
          <Column :expander="true" headerStyle="width: 3rem" />
          
          <Column field="firstname" header="Nombre" :sortable="true">
            <template #body="{ data }">
              <span class="p-column-title">Nombre</span>
              {{ getCustomerName(data) }}
            </template>
          </Column>
          
          <Column field="email" header="Email" :sortable="true">
            <template #body="{ data }">
              <span class="p-column-title">Email</span>
              {{ data.email || '-' }}
            </template>
          </Column>
          
          <Column field="phone" header="Teléfono" :sortable="true">
            <template #body="{ data }">
              <span class="p-column-title">Teléfono</span>
              {{ formatPhone(data) }}
            </template>
          </Column>
          
          <Column field="blacklisted" header="Estado" :sortable="true">
            <template #body="{ data }">
              <span class="p-column-title">Estado</span>
              <Tag
                :value="data.blacklisted ? 'Bloqueado' : 'Activo'"
                :severity="data.blacklisted ? 'danger' : 'success'"
              />
            </template>
          </Column>

          <template #expansion="slotProps">
            <div class="p-3">
              <h5>Reservas de {{ getCustomerName(slotProps.data) }}</h5>
              <DataTable
                :value="customerBookings[slotProps.data.id] || []"
                :loading="loadingBookings[slotProps.data.id]"
                responsiveLayout="scroll"
                :sortable="true"
              >
                <template #empty>
                  Este cliente no tiene reservas.
                </template>
                <template #loading>
                  Cargando reservas...
                </template>
                
                <Column field="bookingCode" header="Código de reserva" :sortable="true">
                  <template #body="{ data }">
                    <span class="p-column-title">Código de reserva</span>
                    {{ data.bookingCode }}
                  </template>
                </Column>
                
                <Column field="bookingDate" header="Fecha de reserva" :sortable="true">
                  <template #body="{ data }">
                    <span class="p-column-title">Fecha de reserva</span>
                    {{ formatDate(data.bookingDate) }}
                  </template>
                </Column>
                
                <Column field="state" header="Estado" :sortable="true">
                  <template #body="{ data }">
                    <span class="p-column-title">Estado</span>
                    <Tag
                      :value="getStateLabel(data.state)"
                      :severity="getStateSeverity(data.state)"
                    />
                  </template>
                </Column>
                
                <Column field="type" header="Tipo" :sortable="true">
                  <template #body="{ data }">
                    <span class="p-column-title">Tipo</span>
                    {{ data.type === 'MANUAL' ? 'Manual' : 'Automática' }}
                  </template>
                </Column>
                
                <Column header="Comentarios">
                  <template #body="{ data }">
                    <span class="p-column-title">Comentarios</span>
                    <div v-if="data.customFields && Object.keys(getFilteredMetafields(data.customFields)).length > 0">
                      <div
                        v-for="(value, key) in getFilteredMetafields(data.customFields)"
                        :key="key"
                        class="mb-1"
                      >
                        <strong>{{ key }}:</strong> {{ value }}
                      </div>
                    </div>
                    <span v-else>-</span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mb-1 {
  margin-bottom: 0.25rem;
}
</style>
